import React, { useContext, useState } from "react";
import {
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Tabs,
  Tab,
  Grid,
  Checkbox,
} from "@mui/material";
import { COVERAGE_FORM } from "@platform/rfp-core";
import { TextField, Typography } from "@platform/design-system";

import {
  BenchmarkSection,
  CommissionSection,
  SectionTitle,
} from "../../components";
import { TabPanel } from "../../../../components";
import { RFP_ACTION_TYPES } from "../../constants";
import { handleNumericFieldChange, isDisabledNextStepFn } from "../utils";
import { RfpContext } from "../../contexts/rfpContext";
import { scrollToTop } from "../../../../utils";
import CoverageButtonWrapper from "../components/coverageButtonWrapper";
import envVariables from "../../../../projectEnvVariables";
import CommentsField from "../components/commentsField";

import FicaOptionSection from "./FicaOptionSection";
import { createTabbedFormValidationHandlers } from "./coveragesValidations";
// TODO(eslint): fix cycle
// eslint-disable-next-line import/no-cycle
import { isValidPlan } from "./coverages";

const {
  PLAN_TYPE,
  PLANS,
  ALTERNATE_PLANS,
  ADDITIONAL_NOTES,
  CONTRIBUTION_TYPE,
  CONTRIBUTION_VALUE,
  CONTRIBUTION_STRUCTURE,
  CURRENT_COMMISSION,
  ENROLLED_LIVES,
  FUNDING_ARRANGEMENT,
  REQUESTED_COMMISSION,
  SINGLE_OPTION,
  TOTAL_EMPLOYER_PAID,
  TOTAL_EMPLOYEE_PAID,
  CONTRIBUTORY,
  GROSS_UP,
  EMPLOYER_CONTRIBUTION_PERCENTAGE,
  EMPLOYER_CONTRIBUTION_AMOUNT,
  FULLY_FUNDED,
  ALTERNATIVE_FUNDED,
  BASE_BUY_UP_OPTION,
  PLAN_1,
  PLAN_2,
  ASO,
  ATP,
  BEN_CALCS,
  CHECK_CUTTING,
  FICA_OPTION,
  FICA_BILLABLE,
  FICA_MATCHING_YES,
  ATP_TYPE,
  ALTERNATIVE_FUNDED_TYPE,
  BEN_CALCS_LABEL,
  CHECK_CUTTING_LABEL,
} = COVERAGE_FORM;

const { UPDATE_SELECTED_COVERAGE } = RFP_ACTION_TYPES;

const initialValues = {
  [CONTRIBUTION_STRUCTURE]: "",
  [CONTRIBUTION_TYPE]: "",
  [CONTRIBUTION_VALUE]: "",
  [ENROLLED_LIVES]: "",
  [FICA_OPTION]: null,
  [FICA_BILLABLE]: null,
  [FUNDING_ARRANGEMENT]: "",
  [CURRENT_COMMISSION]: "",
  [REQUESTED_COMMISSION]: "",
  [ADDITIONAL_NOTES]: "",
  [ALTERNATE_PLANS]: "",
};

export const initialSTDCoveragesDetails = {
  [PLAN_TYPE]: SINGLE_OPTION,
  [PLANS]: [
    {
      ...initialValues,
      name: SINGLE_OPTION,
    },
  ],
};

const ShortTermDisability = ({
  coverage,
  nextCoverage,
  isLastCoverage,
  handleNext,
  handleBack,
  prevCoverage,
  isFirstCoverage,
}) => {
  const { setRfp } = useContext(RfpContext);
  const [activeTab, setActiveTab] = useState(0);

  const handleFormChange = (updatedCoverage, currentPlanKey, field) => {
    let fieldKey = field;
    if (fieldKey === PLAN_TYPE) {
      setActiveTab(0);
      fieldKey = undefined;
    }
    setRfp({
      type: UPDATE_SELECTED_COVERAGE,
      value: updatedCoverage,
      currentPlanKey,
      fieldKey,
    });
  };

  const handleNextAction = () => {
    if (coverage.details[PLAN_TYPE] === SINGLE_OPTION || activeTab === 1) {
      handleNext();
    } else {
      setActiveTab(1);
    }
    scrollToTop();
  };

  const handleBackAction = () => {
    if (coverage.details[PLAN_TYPE] === SINGLE_OPTION || activeTab === 0) {
      handleBack();
    } else {
      setActiveTab(0);
    }
    scrollToTop();
  };

  const backButtonName =
    coverage.details[PLAN_TYPE] === SINGLE_OPTION || activeTab === 0
      ? prevCoverage?.name
      : PLAN_1;

  const nextButtonName =
    coverage.details[PLAN_TYPE] === SINGLE_OPTION || activeTab === 1
      ? nextCoverage?.name
      : PLAN_2;

  const displayNextButton = isLastCoverage
    ? coverage.details[PLAN_TYPE] === BASE_BUY_UP_OPTION && activeTab === 0
    : true;

  const displayBackButton = isFirstCoverage
    ? coverage.details[PLAN_TYPE] === BASE_BUY_UP_OPTION && activeTab === 1
    : true;

  const isDisabledNextStep = isDisabledNextStepFn(
    coverage,
    SINGLE_OPTION,
    activeTab,
  );

  return (
    <>
      <ShortTermDisabilityForm
        coverage={coverage}
        onFormChange={handleFormChange}
        activeTab={activeTab}
        onChangeTab={setActiveTab}
      />
      <CoverageButtonWrapper
        displayBackButton={displayBackButton}
        displayNextButton={displayNextButton}
        backButtonName={backButtonName}
        nextButtonName={nextButtonName}
        handleBackAction={handleBackAction}
        handleNextAction={handleNextAction}
        isDisabled={isDisabledNextStep}
        isBackDisabled={false}
      />
    </>
  );
};

export const ShortTermDisabilityForm = ({
  coverage,
  onFormChange,
  activeTab = 0,
  onChangeTab,
}) => {
  const isCurrentCoverage = coverage?.isCurrentCoverage;
  const currentPlan = coverage?.details[PLANS][activeTab];
  const currentPlanErrors = currentPlan?.errors || {};

  const handleChangeTab = (_, newValue) => {
    if (onChangeTab) {
      onChangeTab(newValue);
    }
  };

  const handlePlanTypeChange = (e) => {
    const { value, name } = e.target;
    const plans =
      value === SINGLE_OPTION
        ? [{ ...initialValues, name: SINGLE_OPTION }]
        : [
            { ...initialValues, name: PLAN_1 },
            { ...initialValues, name: PLAN_2 },
          ];
    const payload = {
      ...coverage,
      details: { ...coverage.details, [name]: value, [PLANS]: plans },
    };

    onFormChange(payload, activeTab, name);
  };

  const handlePlanFormChange = ({ name, value }, planName) => {
    const plans = coverage.details.plans.map((plan) => {
      if (plan.name === planName) {
        // reset contribution value field if contribution type is changed
        if (name === CONTRIBUTION_TYPE) {
          plan[CONTRIBUTION_VALUE] = "";
        }

        // Billable applies only when FICA Matching = Yes; when moving off Yes,
        // clear the value AND drop any stale "Billable required" error in the
        // same update (a second dispatch would race this one).
        if (name === FICA_OPTION && value !== FICA_MATCHING_YES) {
          const remainingErrors = { ...(plan.errors || {}) };
          delete remainingErrors[FICA_BILLABLE];

          return {
            ...plan,
            [name]: value,
            [FICA_BILLABLE]: null,
            errors: remainingErrors,
          };
        }

        return {
          ...plan,
          [name]: value,
        };
      }
      return plan;
    });

    const payload = {
      ...coverage,
      details: { ...coverage.details, [PLANS]: plans },
    };

    onFormChange(payload, activeTab, name);
  };

  const { handlePlanFormChangeWithValidation, handlePreviousFieldsValidation } =
    createTabbedFormValidationHandlers({
      currentPlan,
      coverage,
      onFormChange,
      isCurrentCoverage,
      activeTab,
    });

  const isTabError = (tab) =>
    !isValidPlan(coverage?.details[PLANS][tab]?.errors);

  const planForm = (planName) => (
    <>
      {isCurrentCoverage && (
        <>
          <SectionTitle>Enrolled Lives</SectionTitle>
          <Grid container spacing={2} px={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                autoComplete="off"
                fullWidth
                name={ENROLLED_LIVES}
                error={!!currentPlanErrors[ENROLLED_LIVES]}
                value={currentPlan[ENROLLED_LIVES] || ""}
                onChange={(e) => {
                  handleNumericFieldChange(
                    e.target,
                    planName,
                    handlePlanFormChange,
                  );
                }}
                onFocus={() => {
                  handlePreviousFieldsValidation(ENROLLED_LIVES, planName);
                }}
                onBlur={(e) => {
                  handlePlanFormChangeWithValidation(e.target, planName);
                }}
                label={
                  <Typography>
                    Number of Enrolled Members{" "}
                    <span className={"required"}>*</span>
                  </Typography>
                }
              />
              {!!currentPlanErrors && (
                <Typography variant="caption" color="danger">
                  {currentPlanErrors[ENROLLED_LIVES]}
                </Typography>
              )}
            </Grid>
          </Grid>
        </>
      )}

      <SectionTitle>Contribution Structure</SectionTitle>
      <Box pl={2} pr={2}>
        <FormControl>
          <FormLabel id="contribution-structure-label" required>
            Select a Contribution Structure
          </FormLabel>
          <RadioGroup
            value={currentPlan[CONTRIBUTION_STRUCTURE]}
            row
            aria-labelledby="contribution-structure-label"
            name={CONTRIBUTION_STRUCTURE}
            onChange={(e) => {
              handlePlanFormChange(e.target, planName);
            }}
            onFocus={() => {
              handlePreviousFieldsValidation(CONTRIBUTION_STRUCTURE, planName);
            }}
            onBlur={(e) => {
              handlePlanFormChangeWithValidation(e.target, planName);
            }}
          >
            <FormControlLabel
              value={TOTAL_EMPLOYER_PAID}
              control={<Radio />}
              label={TOTAL_EMPLOYER_PAID}
            />
            <FormControlLabel
              value={TOTAL_EMPLOYEE_PAID}
              control={<Radio />}
              label={TOTAL_EMPLOYEE_PAID}
            />
            <FormControlLabel
              value={CONTRIBUTORY}
              control={<Radio />}
              label={CONTRIBUTORY}
            />
            <FormControlLabel
              value={GROSS_UP}
              control={<Radio />}
              label={GROSS_UP}
            />
          </RadioGroup>
        </FormControl>

        {currentPlan[CONTRIBUTION_STRUCTURE] === CONTRIBUTORY && (
          <Box mt={2}>
            <FormControl>
              <FormLabel id="contribution-amount-label">
                Add Contribution amount
              </FormLabel>
              <Box mt={1}>
                <RadioGroup
                  value={currentPlan[CONTRIBUTION_TYPE]}
                  aria-labelledby="contribution-amount-label"
                  name={CONTRIBUTION_TYPE}
                  onChange={(e) => {
                    handlePlanFormChange(e.target, planName);
                  }}
                  onFocus={() => {
                    handlePreviousFieldsValidation(CONTRIBUTION_TYPE, planName);
                  }}
                  onBlur={(e) => {
                    handlePlanFormChangeWithValidation(e.target, planName);
                  }}
                >
                  <FormControlLabel
                    value={EMPLOYER_CONTRIBUTION_PERCENTAGE}
                    control={<Radio />}
                    label={
                      <Box display="flex" alignItems="center" fullWidth>
                        <Typography noWrap mr={1}>
                          {EMPLOYER_CONTRIBUTION_PERCENTAGE}
                        </Typography>
                        {currentPlan[CONTRIBUTION_TYPE] ===
                          EMPLOYER_CONTRIBUTION_PERCENTAGE && (
                          <TextField
                            autoComplete="off"
                            size="sm"
                            name={CONTRIBUTION_VALUE}
                            value={currentPlan[CONTRIBUTION_VALUE] || ""}
                            onChange={(e) => {
                              handlePlanFormChange(e.target, planName);
                            }}
                            onFocus={() => {
                              handlePreviousFieldsValidation(
                                CONTRIBUTION_VALUE,
                                planName,
                              );
                            }}
                            onBlur={(e) => {
                              handlePlanFormChangeWithValidation(
                                e.target,
                                planName,
                              );
                            }}
                            label="% percentage"
                          />
                        )}
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value={EMPLOYER_CONTRIBUTION_AMOUNT}
                    control={<Radio />}
                    label={
                      <Box display="flex" alignItems="center" fullWidth>
                        <Typography noWrap mr={1}>
                          {EMPLOYER_CONTRIBUTION_AMOUNT}
                        </Typography>
                        {currentPlan[CONTRIBUTION_TYPE] ===
                          EMPLOYER_CONTRIBUTION_AMOUNT && (
                          <TextField
                            autoComplete="off"
                            size="sm"
                            name={CONTRIBUTION_VALUE}
                            value={currentPlan[CONTRIBUTION_VALUE] || ""}
                            label="$ amount"
                            onChange={(e) => {
                              handlePlanFormChange(e.target, planName);
                            }}
                            onFocus={() => {
                              handlePreviousFieldsValidation(
                                CONTRIBUTION_VALUE,
                                planName,
                              );
                            }}
                            onBlur={(e) => {
                              handlePlanFormChangeWithValidation(
                                e.target,
                                planName,
                              );
                            }}
                          />
                        )}
                      </Box>
                    }
                  />
                </RadioGroup>
              </Box>
            </FormControl>
          </Box>
        )}
        <Box>
          {!!currentPlanErrors && (
            <Typography variant="caption" color="danger">
              {currentPlanErrors[CONTRIBUTION_STRUCTURE]}{" "}
              {currentPlanErrors[CONTRIBUTION_VALUE]}
            </Typography>
          )}
        </Box>
      </Box>
      <FicaOptionSection
        currentPlan={currentPlan}
        currentPlanErrors={currentPlanErrors}
        planName={planName}
        handlePlanFormChange={handlePlanFormChange}
        handlePreviousFieldsValidation={handlePreviousFieldsValidation}
        handlePlanFormChangeWithValidation={handlePlanFormChangeWithValidation}
      />

      <SectionTitle>Funding Arrangement</SectionTitle>
      <Box pl={2} pr={2}>
        <FormControl>
          <FormLabel id="funding-arrangement-label" required>
            Select a Funding Arrangement
          </FormLabel>
          <RadioGroup
            value={currentPlan[FUNDING_ARRANGEMENT]}
            row
            aria-labelledby="funding-arrangement-label"
            name={FUNDING_ARRANGEMENT}
            onChange={(e) => {
              handlePlanFormChange(e.target, planName);
            }}
            onFocus={() => {
              handlePreviousFieldsValidation(FUNDING_ARRANGEMENT, planName);
            }}
            onBlur={(e) => {
              handlePlanFormChangeWithValidation(e.target, planName);
            }}
          >
            <FormControlLabel
              value={FULLY_FUNDED}
              control={<Radio />}
              label={FULLY_FUNDED}
            />
            <FormControlLabel
              value={ALTERNATIVE_FUNDED}
              control={<Radio />}
              label={ALTERNATIVE_FUNDED}
            />
          </RadioGroup>
        </FormControl>
        <Box>
          {!!currentPlanErrors && (
            <Typography variant="caption" color="danger">
              {currentPlanErrors[FUNDING_ARRANGEMENT]}
            </Typography>
          )}
        </Box>
        {currentPlan[FUNDING_ARRANGEMENT] === ALTERNATIVE_FUNDED && (
          <>
            <FormControl>
              <FormLabel id="alternative-funded-label" required sx={{ my: 1 }}>
                Confirm Plan Type
              </FormLabel>
              <RadioGroup
                value={currentPlan[ALTERNATIVE_FUNDED_TYPE] || ""}
                row
                aria-labelledby="alternative-funded-label"
                name={ALTERNATIVE_FUNDED_TYPE}
                onChange={(e) => {
                  handlePlanFormChange(e.target, planName);
                }}
              >
                <FormControlLabel value={ASO} control={<Radio />} label={ASO} />
                <FormControlLabel value={ATP} control={<Radio />} label={ATP} />
              </RadioGroup>
            </FormControl>
            <Box>
              {!!currentPlanErrors && (
                <Typography variant="caption" color="danger">
                  {currentPlanErrors[ALTERNATIVE_FUNDED_TYPE]}
                </Typography>
              )}
            </Box>
            {currentPlan[ALTERNATIVE_FUNDED_TYPE] === ATP && (
              <>
                <FormControl>
                  <FormControlLabel
                    name={BEN_CALCS}
                    onChange={({ target }) => {
                      handlePlanFormChange(
                        { name: target.name, value: target.checked },
                        planName,
                      );
                    }}
                    value={BEN_CALCS}
                    checked={!!currentPlan[BEN_CALCS]}
                    control={<Checkbox />}
                    label={BEN_CALCS_LABEL}
                  />
                  <FormControlLabel
                    name={CHECK_CUTTING}
                    onChange={({ target }) => {
                      handlePlanFormChange(
                        { name: target.name, value: target.checked },
                        planName,
                      );
                    }}
                    value={CHECK_CUTTING}
                    checked={!!currentPlan[CHECK_CUTTING]}
                    control={<Checkbox />}
                    label={CHECK_CUTTING_LABEL}
                  />
                </FormControl>
                <Box>
                  {!!currentPlanErrors && (
                    <Typography variant="caption" color="danger">
                      {currentPlanErrors[ATP_TYPE]}
                    </Typography>
                  )}
                </Box>
              </>
            )}
          </>
        )}
      </Box>

      <CommissionSection
        handlePlanFormChange={handlePlanFormChange}
        handlePlanFormChangeWithValidation={handlePlanFormChangeWithValidation}
        handlePreviousFieldsValidation={handlePreviousFieldsValidation}
        currentPlanErrors={currentPlanErrors}
        currentPlan={currentPlan}
        planName={planName}
        isCurrentCoverage={isCurrentCoverage}
      />

      {!isCurrentCoverage && (
        <BenchmarkSection
          handlePlanFormChange={handlePlanFormChange}
          currentPlan={currentPlan}
          planName={planName}
          link={envVariables.VITE_REACT_APP_STD_BENCHMARKS_URL || "/"}
          coverageName={coverage?.name}
        />
      )}

      <CommentsField
        currentPlan={currentPlan}
        onChange={(e) => {
          handlePlanFormChange(e.target, planName);
        }}
        handlePreviousFieldsValidation={handlePreviousFieldsValidation}
      />
    </>
  );

  const currentPlanName =
    coverage.details[PLAN_TYPE] === SINGLE_OPTION
      ? SINGLE_OPTION
      : activeTab === 0
        ? PLAN_1
        : PLAN_2;

  return (
    <>
      <SectionTitle>Plan Type</SectionTitle>
      <Box pl={2} pr={2}>
        <FormControl>
          <FormLabel id="plan-type-label" required>
            Select a Plan Type
          </FormLabel>
          <RadioGroup
            value={coverage.details[PLAN_TYPE]}
            row
            aria-labelledby="plan-type-label"
            name={PLAN_TYPE}
            onChange={handlePlanTypeChange}
          >
            <FormControlLabel
              value={SINGLE_OPTION}
              control={<Radio />}
              label={SINGLE_OPTION}
            />
            <FormControlLabel
              value={BASE_BUY_UP_OPTION}
              control={<Radio />}
              label={BASE_BUY_UP_OPTION}
            />
          </RadioGroup>
        </FormControl>
      </Box>

      {coverage.details[PLAN_TYPE] === BASE_BUY_UP_OPTION ? (
        <>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }} mt={5}>
            <Tabs
              variant="fullWidth"
              className="detail-page-tabs"
              value={activeTab}
              onChange={handleChangeTab}
              aria-label="client detail tabs"
            >
              <Tab
                sx={{ maxWidth: "50%" }}
                required
                label={
                  <Typography className={isTabError(0) ? "error" : ""}>
                    PLAN 1
                  </Typography>
                }
              />
              <Tab
                required
                label={
                  <Typography className={isTabError(1) ? "error" : ""}>
                    PLAN 2
                  </Typography>
                }
              />
            </Tabs>
          </Box>
          <TabPanel value={activeTab} index={activeTab}>
            {planForm(currentPlanName)}
          </TabPanel>
        </>
      ) : (
        <Box sx={{ borderBottom: 1, borderColor: "divider" }} mt={5}>
          {planForm(currentPlanName)}
        </Box>
      )}
    </>
  );
};

export default ShortTermDisability;
